import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Calendar, Users, AlertCircle, Clock,
  UserMinus, Heart, ArrowLeft
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";

export default function EligibilityMilestones() {
  const [, setLocation] = useLocation();

  // Get authenticated user to read clientId for scoping
  const { data: user } = useQuery<any>({ queryKey: ["/api/auth/user"] });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation("/")}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold">Eligibility Milestones</h1>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Dependents Aging Out
            </CardTitle>
            <UserMinus className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">—</div>
            <p className="text-xs text-muted-foreground">
              Turning 26 within 90 days
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Medicare Eligible
            </CardTitle>
            <Heart className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">—</div>
            <p className="text-xs text-muted-foreground">
              Turning 65 within 9 months
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Action Required
            </CardTitle>
            <AlertCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">—</div>
            <p className="text-xs text-muted-foreground">
              Upcoming deadlines
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="aging-out" className="space-y-4">
        <TabsList>
          <TabsTrigger value="aging-out" className="gap-2">
            <UserMinus className="h-4 w-4" />
            Dependents Aging Out
          </TabsTrigger>
          <TabsTrigger value="medicare" className="gap-2">
            <Heart className="h-4 w-4" />
            Medicare Eligibility
          </TabsTrigger>
        </TabsList>

        {/* Dependents Aging Out Tab */}
        <TabsContent value="aging-out" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserMinus className="h-5 w-5" />
                Dependents Turning 26
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Dependents who will lose eligibility when they turn 26. Coverage typically ends at the end of the month of their 26th birthday.
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* 90 Days Section */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      90 Days
                    </Badge>
                    <span className="text-sm text-muted-foreground">Plenty of time to plan</span>
                  </div>
                  <div className="text-sm text-muted-foreground text-center py-8">
                    No dependents aging out in 90 days
                  </div>
                </div>

                {/* 60 Days Section */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                      60 Days
                    </Badge>
                    <span className="text-sm text-muted-foreground">Start planning transition</span>
                  </div>
                  <div className="text-sm text-muted-foreground text-center py-8">
                    No dependents aging out in 60 days
                  </div>
                </div>

                {/* 30 Days Section */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                      30 Days
                    </Badge>
                    <span className="text-sm text-muted-foreground">Urgent - action required</span>
                  </div>
                  <div className="text-sm text-muted-foreground text-center py-8">
                    No dependents aging out in 30 days
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                    About Dependent Aging Out
                  </p>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Under the ACA, dependents can remain on a parent's health insurance plan until they turn 26.
                    After turning 26, they'll need to find alternative coverage through their employer,
                    the marketplace, or other options. COBRA continuation may also be available.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Medicare Eligibility Tab */}
        <TabsContent value="medicare" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="h-5 w-5" />
                Employees Approaching Medicare Eligibility
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Employees who will become eligible for Medicare when they turn 65. They should be notified about their options well in advance.
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* 9 Months Section */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      9 Months
                    </Badge>
                    <span className="text-sm text-muted-foreground">Initial notification period</span>
                  </div>
                  <div className="text-sm text-muted-foreground text-center py-8">
                    No employees reaching Medicare eligibility in 9 months
                  </div>
                </div>

                {/* 6 Months Section */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                      6 Months
                    </Badge>
                    <span className="text-sm text-muted-foreground">Medicare enrollment planning</span>
                  </div>
                  <div className="text-sm text-muted-foreground text-center py-8">
                    No employees reaching Medicare eligibility in 6 months
                  </div>
                </div>

                {/* 3 Months Section */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                      3 Months
                    </Badge>
                    <span className="text-sm text-muted-foreground">Enrollment window approaching</span>
                  </div>
                  <div className="text-sm text-muted-foreground text-center py-8">
                    No employees reaching Medicare eligibility in 3 months
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                    About Medicare Eligibility
                  </p>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Employees become eligible for Medicare at age 65. The Initial Enrollment Period (IEP)
                    begins 3 months before their 65th birthday and ends 3 months after. If they're still
                    working and have employer coverage, they may choose to delay Medicare enrollment, but
                    should understand the implications for their benefits coordination.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
